import { Condition, Patient } from '@medplum/fhirtypes'
import dayjs from 'dayjs'
import { selectProviderOrderPreferences } from 'fhir/Organization/helpers'
import {
  getCurrentCertPeriod,
  getNextCertPeriod,
  getPatientActiveAuthorization,
  getPatientAuthorizations,
  getPatientCertPeriods,
  getPatientIdentifiers,
  getPatientSex,
  getPrimaryCareProvider,
  getPrimaryLanguage
} from 'fhir/Patient/helpers'
import {
  BaseConsentFragment,
  PatientData_CareTeamListFragment,
  PatientData_PatientCareDataFragment
} from 'generated/graphql'
import { PatientSchema } from 'store/patient'
import { formatAddress, getPrimaryAddress } from 'utils/address'
import { formatPrimaryName } from 'utils/names'
import { captureException } from 'utils/sentry'
import { selectCareTeamPractitionersWithRole, selectPrimaryCareTeam } from 'fhir/CareTeam/helpers'
import { CareTeamMemberRole } from 'fhir/CareTeam/constants'
import { getPractitionerTimezone } from 'fhir/Practitioner/helpers'
import { DEFAULT_PRACTITIONER_TIMEZONE } from 'fhir/Practitioner/constants'
import { ServiceTypeName } from 'fhir/HealthcareService/constants'
import { castToEnum } from 'utils/enum'
import { getIdFromReference } from 'fhir/utils'

export const serviceTypeExtractor = (
  serviceTypeExtensions: PatientData_PatientCareDataFragment['serviceType']
): ServiceTypeName[] => {
  const serviceTypes: ServiceTypeName[] = []

  serviceTypeExtensions?.[0]?.extension?.forEach((ext) => {
    const serviceType = castToEnum(ServiceTypeName, ext.valueReference?.display ?? undefined)
    if (serviceType) {
      serviceTypes.push(serviceType)
    }
  })

  return serviceTypes
}

export const serviceTypeIdExtractor = (
  serviceTypeExtensions: PatientData_PatientCareDataFragment['serviceType']
): string[] => {
  const serviceTypeIds = new Set<string>()

  const addServiceTypeId = (
    valueReference?: { id?: string | null; reference?: string | null } | null
  ): void => {
    if (!valueReference) return

    const serviceTypeId = valueReference.id ?? getIdFromReference(valueReference.reference ?? '')
    if (serviceTypeId) {
      serviceTypeIds.add(serviceTypeId)
    }
  }

  serviceTypeExtensions?.forEach((serviceTypeExtension) => {
    addServiceTypeId(serviceTypeExtension.valueReference)
    serviceTypeExtension.extension?.forEach((ext) => {
      addServiceTypeId(ext.valueReference)
    })
  })

  return Array.from(serviceTypeIds)
}

export const formatPatientData = (
  patient: PatientData_PatientCareDataFragment
): PatientSchema | undefined => {
  const patientId = patient.id
  const name = formatPrimaryName(patient.name)
  const patientAddress = getPrimaryAddress(patient.address ?? []) ?? undefined
  const addressDisplay = formatAddress(patientAddress)
  const preferredName = formatPrimaryName(patient.name, { type: 'usual' })
  const identifiers = getPatientIdentifiers(patient)
  const primaryLanguage = getPrimaryLanguage(patient)
  const birthSexExtension = getPatientSex(patient)
  const ethnicityExtension = patient.ethnicityExtension?.[0]?.valueString ?? null
  const careLocation1 = patient.authorizedLocation1?.[0]?.valueReference?.id ?? null
  const careLocation2 = patient.authorizedLocation2?.[0]?.valueReference?.id ?? null
  const serviceTypes: ServiceTypeName[] = patient.serviceType
    ? serviceTypeExtractor(patient.serviceType)
    : []

  const managingOrganization = patient?.managingOrganization ?? null
  const careTeamList =
    patient?.CareTeamList?.filter((c): c is PatientData_CareTeamListFragment => !!c) ?? []

  const careTeam = selectPrimaryCareTeam(careTeamList)
  const caregivers = careTeam
    ? selectCareTeamPractitionersWithRole(careTeam, CareTeamMemberRole.Caregiver)
    : []

  // this is bad, we should enforce caregiver timezone from the source of truth, doing this before we prioritize data quality.
  const caregiverTimezone =
    getPractitionerTimezone(caregivers.at(0)) ?? DEFAULT_PRACTITIONER_TIMEZONE

  const conditions = patient?.conditions?.filter((c): c is Partial<Condition> => !!c) ?? []
  const primaryCareProvider = patient.generalPractitioner
    ? getPrimaryCareProvider(patient.generalPractitioner as Patient['generalPractitioner'])
    : null
  const providerOrderPreferences = selectProviderOrderPreferences(primaryCareProvider)
  const activeAuthorization = getPatientActiveAuthorization(
    patient,
    caregiverTimezone
  ) as BaseConsentFragment | null
  const authorizations = getPatientAuthorizations(patient) ?? []

  if (!patientId) {
    captureException(new Error('Patient ID not found'), { extras: { info: patient } })
    return
  }
  if (!name) {
    captureException(new Error(`Patient name not found for patient ID:${patientId}`), {
      extras: { info: patient }
    })
    return
  }

  if (!identifiers?.mrn) {
    captureException(new Error(`Patient MRN not found for patient ID:${patientId}`), {
      extras: { resources: { patient } }
    })
    return
  }
  const currentCertPeriods = getPatientCertPeriods(patient) || []
  const currentEpisodeOfCare = getCurrentCertPeriod(
    currentCertPeriods,
    caregiverTimezone,
    false,
    true
  )

  const patientCertPeriod =
    currentEpisodeOfCare?.period?.start && currentEpisodeOfCare.period.end
      ? {
          start: dayjs(currentEpisodeOfCare.period.start),
          end: dayjs(currentEpisodeOfCare.period.end)
        }
      : null

  const formattedData: PatientSchema = {
    id: patientId,
    name,
    dateOfBirth: patient.birthDate ? dayjs(patient.birthDate) : null,
    gender: patient?.gender ?? null,
    ssn: identifiers?.ssn || null,
    mrn: identifiers?.mrn,
    primaryCareProvider: primaryCareProvider,
    providerOrderPreferences,
    primaryLanguage,
    preferredName,
    patientStatus: patient.active ? 'active' : 'inactive',
    admissionStatus: patient.admissionStatusExtension?.[0]?.valueString ?? 'N/A',
    conditions,
    sex: birthSexExtension,
    ethnicity: ethnicityExtension,
    addressLine: patientAddress?.line?.[0] ?? null,
    addressLine2: patientAddress?.line?.[1] ?? null,
    addressCity: patientAddress?.city ?? null,
    addressState: patientAddress?.state ?? null,
    addressPostalCode: patientAddress?.postalCode ?? null,
    addressDisplay: addressDisplay ?? null,
    careLocation1,
    careLocation2,
    activeAuthorization,
    authorizations,
    currentCertPeriods,
    careTeam: careTeamList,
    caregiverTimezone,
    serviceTypes,
    managingOrganization,
    careTeamId: careTeamList.at(0)?.id ?? null,

    currentEpisodeOfCare,
    nextEpisodeOfCare: getNextCertPeriod(currentCertPeriods),
    currentCertPeriod: patientCertPeriod
  }
  return formattedData
}
